import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { useListContext } from '../../list-context';

import {
  IconHeartOutlined,
  IconHeartFilled,
  IconNotificationFilled,
} from '@prisjakt/ui/icon';
import { Dropdown, DropdownContent } from '@prisjakt/ui/dropdown';
import { ButtonIcon, ButtonSize, ButtonType } from '@prisjakt/ui/button';
import { TextButton } from '@prisjakt/ui/button/variants';
import { Direction } from '@prisjakt/ui';
import LoginPromoModal from 'src/components/modal/login-promo';
import {
  openAlerts,
  removeAlertRequest,
} from 'src/areas/sidebar/actions/alert'; // Should not depend on other areas
import {
  selectList,
  addListItemRequest,
  addItemToMyProductsRequest,
} from 'src/areas/sidebar/actions/lists'; // Should not depend on other areas
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';
import { SaveToListButton } from './save-button';
import { CompareButton } from './compare-button';
import { AlertButton } from './alert-button';

import { AppState } from 'src/reducers';

import { useOnAction } from '../../utils';

import { ProductPageQuery } from 'src/types';
import { UserProductAlert } from '../../../../../sidebar/types';
import { Me } from '../../types';

import {
  ActionButton,
  ButtonText,
  DropdownItem,
  DropdownToggleButton,
  ProductActionButtonWrapper,
  SavedListWrapper,
  Tooltip,
} from './styles';

type Props = {
  product: NonNullable<ProductPageQuery['product']>;
  className?: string;
};

export const ActionButtonsDesktop = ({ className, product }: Props) => {
  const { t } = useAppContext();
  const { fetchMoreLists, moreLists, pageInfo } = useListContext();

  const dispatch = useDispatch();

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const isAuthenticated = useIsAuthenticated();
  const userProductAlerts = useSelector(
    (state: AppState) => state.areas.sidebar.alerts.products
  );
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );

  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const toggleLoginPromo = () => setLoginPromoState(!showLoginPromo);

  const onAction = useOnAction();

  const addToList = () => {
    onAction('list-add');
    dispatch(addListItemRequest(product.id, product.name, true));

    setAddedToListState(true);
  };
  const latestLists =
    me?.lists?.nodes
      ?.filter(list => deletedLists[list.id] !== true)
      .filter(list => !list.inventory)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      }) ?? [];

  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );

  const alertId = userAlert && userAlert.alertId;

  const removeAlert = () => {
    if (!alertId) return;

    onAction('alert-remove');
    dispatch(openAlerts());
    dispatch(removeAlertRequest(alertId, product.id));
  };

  const editAlert = () => {
    dispatch(openAlerts());
    onAction('alert-create');
  };

  const addToMyProducts = () => {
    onAction('list-add');
    dispatch(addItemToMyProductsRequest(product.id, product.name, true));
    setAddedToListState(true);
  };

  const isBookmarked = alertId && isAuthenticated;

  const onAlertClickHandler = isBookmarked
    ? undefined
    : () => {
        onAction('alert-create');
        return false;
      };

  return (
    <ProductActionButtonWrapper className={className}>
      <ActionButton data-test="SaveToListButton">
        {isAuthenticated ? (
          <Tooltip
            title={t('product', 'action-button.list-tooltip')}
            placement={Direction.Down}
            zIndex={1}
          >
            <Dropdown>
              <DropdownToggleButton
                ariaLabel={t('common', 'button.save')}
                type={ButtonType.TONAL}
                size={ButtonSize.SMALL}
              >
                <ButtonIcon>
                  {addedToList ? <IconHeartFilled /> : <IconHeartOutlined />}
                </ButtonIcon>
                <ButtonText>{t('common', 'button.save')}</ButtonText>
              </DropdownToggleButton>
              <DropdownContent>
                <SavedListWrapper>
                  <DropdownItem onClick={addToMyProducts}>
                    {t('lists', 'my-products.title')}
                  </DropdownItem>
                  {[...latestLists, ...(moreLists || [])].map(list => (
                    <DropdownItem
                      onClick={() => {
                        dispatch(selectList(list.id));
                        addToList();
                      }}
                      key={list.id}
                    >
                      {list.name}
                    </DropdownItem>
                  ))}

                  {pageInfo?.hasNext && (
                    <TextButton onClick={fetchMoreLists}>
                      {t('common', 'button.show-more')}
                    </TextButton>
                  )}
                </SavedListWrapper>
              </DropdownContent>
            </Dropdown>
          </Tooltip>
        ) : (
          <>
            {showLoginPromo && (
              <LoginPromoModal
                isOpen={showLoginPromo}
                onRequestClose={toggleLoginPromo}
              />
            )}
            <SaveToListButton
              isActive={addedToList}
              onClick={toggleLoginPromo}
            />
          </>
        )}
      </ActionButton>

      <ActionButton data-test="CreateAlertButton">
        {isBookmarked ? (
          <Tooltip
            placement={Direction.Down}
            title={t('product', 'action-button.alert-tooltip')}
            zIndex={1}
          >
            <Dropdown>
              <DropdownToggleButton
                ariaLabel={t('product', 'alert.create-button')}
                type={ButtonType.TONAL}
                size={ButtonSize.SMALL}
              >
                <ButtonIcon>
                  <IconNotificationFilled />
                </ButtonIcon>
                <ButtonText>{t('product', 'alert.create-button')}</ButtonText>
              </DropdownToggleButton>
              <DropdownContent>
                <DropdownItem onClick={removeAlert}>
                  {t('product', 'alert.delete-button')}
                </DropdownItem>

                <DropdownItem onClick={editAlert}>
                  {t('product', 'alert.edit-button')}
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          </Tooltip>
        ) : (
          <AlertButton isActive={false} onClick={onAlertClickHandler} />
        )}
      </ActionButton>

      <ActionButton data-test="CompareButton">
        <CompareButton product={product} />
      </ActionButton>
    </ProductActionButtonWrapper>
  );
};
